import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Briefcase } from 'lucide-react';
import { useToast } from '@chakra-ui/react';
import { CONTACT_FORM_URL } from '../utils/constants';
import { BotTrapField, useFormBotTrap } from '../components/FormBotTrap';
import logger from '../utils/logger';
import { SiteNav } from '../components/SiteNav';
import { SiteFooter } from '../components/SiteFooter';

const MotionDiv = motion.div;

const CONSULTING_EMAIL = 'info@propvetter.com';

// Two-style CTA system (locked in by designer review). Mirrors the
// definitions in Landing.tsx so the same button shape carries across pages.
const btnPrimary =
  'inline-flex items-center justify-center bg-gsp-orange text-white font-semibold px-7 py-4 rounded-[10px] hover:bg-gsp-orange/90 transition-colors shadow-lg hover:shadow-xl';
const btnSecondary =
  'inline-flex items-center justify-center border-2 border-gsp-orange text-gsp-orange font-semibold px-7 py-4 rounded-[10px] hover:bg-gsp-orange/10 transition-colors';

// Inline SVG icons — recreated from the design verbatim for pixel-perfect
// fidelity (paths and stroke widths kept identical to the source HTML).
const ImplementationIcon = () => (
  <svg className="w-7 h-7 text-gsp-orange" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
    <path strokeLinecap="round" strokeLinejoin="round" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
  </svg>
);

const EmailIcon = () => (
  <svg className="w-5 h-5 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
  </svg>
);

const GlobeIcon = () => (
  <svg className="w-5 h-5 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path strokeLinecap="round" strokeLinejoin="round" d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9" />
  </svg>
);

const BuildingIcon = () => (
  <svg className="w-5 h-5 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path strokeLinecap="round" strokeLinejoin="round" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
  </svg>
);

// Grid backgrounds — match the design's `.grid-bg` (teal, with vertical fade
// mask) and `.grid-bg-light` (subtle white grid for navy panels).
function GridBg() {
  // Mask uses symmetric stops at 25% / 75% so the fade-in zone above and
  // fade-out zone below are exactly equal lengths. The previous 18/82 was
  // also symmetric, but the navy announcement bar sitting directly on top
  // of the hero made the top edge perceptually harsher; widening both
  // fade zones softens that contrast without breaking symmetry.
  return (
    <div
      className="absolute inset-0 pointer-events-none opacity-60"
      style={{
        backgroundImage: `
          linear-gradient(to right, rgba(51, 130, 156, 0.28) 1px, transparent 1px),
          linear-gradient(to bottom, rgba(51, 130, 156, 0.28) 1px, transparent 1px)
        `,
        backgroundSize: '50px 50px',
        WebkitMaskImage: 'linear-gradient(to bottom, transparent 0%, black 25%, black 75%, transparent 100%)',
        maskImage: 'linear-gradient(to bottom, transparent 0%, black 25%, black 75%, transparent 100%)',
      }}
    />
  );
}

function GridBgLight({ opacityClass = 'opacity-50' }: { opacityClass?: string }) {
  return (
    <div
      className={`absolute inset-0 pointer-events-none ${opacityClass}`}
      style={{
        backgroundImage: `
          linear-gradient(to right, rgba(255,255,255,0.10) 1px, transparent 1px),
          linear-gradient(to bottom, rgba(255,255,255,0.10) 1px, transparent 1px)
        `,
        backgroundSize: '48px 48px',
      }}
    />
  );
}

function DiagOrnament({ className, sizeClass = 'w-72 h-72' }: { className: string; sizeClass?: string }) {
  return (
    <div className={`absolute ${className} ${sizeClass} text-gsp-teal pointer-events-none`}>
      <svg viewBox="0 0 200 200" fill="none">
        <path d="M0 200L200 0V60L0 200Z" fill="currentColor" />
        <path d="M60 200L200 60V120L120 200H60Z" fill="currentColor" />
        <path d="M120 200L200 120V200H120Z" fill="currentColor" />
      </svg>
    </div>
  );
}

// Stat numerals — tabular, tight tracking, snug line height. Mirrors the
// `.stat-num` utility in the design CSS.
const statNumStyle: React.CSSProperties = {
  fontVariantNumeric: 'tabular-nums',
  letterSpacing: '-0.04em',
  lineHeight: 0.92,
};

function Consulting() {
  const location = useLocation();
  const toast = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const botTrap = useFormBotTrap();
  const [form, setForm] = useState({ name: '', firm: '', email: '', role: '', message: '' });
  const [emailCopied, setEmailCopied] = useState(false);

  // See Landing.tsx for the rationale — mailto still fires for users with
  // a configured mail client, and we copy to clipboard as a fallback.
  const handleEmailClick = () => {
    try {
      navigator.clipboard?.writeText(CONSULTING_EMAIL);
    } catch {
      /* ignore */
    }
    setEmailCopied(true);
    window.setTimeout(() => setEmailCopied(false), 2000);
  };

  const scrollToSection = (id: string) => {
    const el = document.getElementById(id);
    if (!el) return;
    const top = el.getBoundingClientRect().top + window.pageYOffset - 80;
    window.scrollTo({ top, behavior: 'smooth' });
  };

  useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace('#', '');
      setTimeout(() => scrollToSection(id), 50);
    }
  }, [location.hash]);

  const onChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setForm({ ...form, [e.target.id]: e.target.value });
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Pack advisory-specific fields (firm, role) into the message field so
    // the existing /api/contact endpoint (name/email/phone/message) doesn't
    // need a backend change.
    const payload = {
      name: form.name,
      email: form.email,
      phone: '',
      ...botTrap.signals(),
      message: `[Advisory inquiry]\nFirm: ${form.firm || '—'}\nRole: ${form.role || '—'}\n\n${form.message}`,
    };

    try {
      const response = await fetch(CONTACT_FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await response.json();
      if (!data.success) throw new Error(data.message || 'Failed to send inquiry');
      toast({
        title: 'Inquiry sent.',
        description: "Thanks. We'll get back to you with a scoped engagement.",
        status: 'success',
        duration: 5000,
        isClosable: true,
        position: 'bottom',
      });
      setForm({ name: '', firm: '', email: '', role: '', message: '' });
      botTrap.reset();
    } catch (err) {
      logger.error('Error sending advisory inquiry:', err);
      toast({
        title: 'Could not send inquiry',
        description: 'Please try again or email us directly.',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'bottom',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gsp-bg-light text-gsp-text-primary flex flex-col">
      <SiteNav currentPage="consulting" />

      {/* Announcement Banner — same as the home page so the global ribbon
          stays consistent across surfaces. Routes back to /#announcement so
          Landing's hash-scroll effect lands on the announcement section. */}
      <div className="bg-gsp-navy">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 text-center">
          <a
            href="/#announcement"
            className="text-white text-sm hover:text-gsp-orange transition-colors"
          >
            <span className="hidden sm:inline"><strong>Announcement:</strong> <span className="underline">AXCS Capital Acquires Propvetter</span></span>
            <span className="sm:hidden"><span className="underline">AXCS Acquires Propvetter</span></span>
          </a>
        </div>
      </div>

      {/* Hero — fills the viewport (minus nav + announcement bar) and
          vertically-centers the headline block, matching the Landing hero
          pattern. */}
      <section id="top" className="relative bg-gray-50 overflow-hidden min-h-[calc(100vh-120px)] flex flex-col">
        <GridBg />
        <DiagOrnament className="-top-10 -right-10 opacity-10" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex-1 flex items-center justify-center py-12">
          <MotionDiv
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, ease: [0.22, 0.61, 0.36, 1] }}
            className="max-w-5xl mx-auto text-center"
          >
            {/* Eyebrow badge removed (designer review: decorative, no
                info value). */}
            <h1 className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold tracking-tight text-gsp-text-primary" style={{ lineHeight: 1.05 }}>
              <span className="md:whitespace-nowrap">
                AI strategy &amp; <span className="text-gsp-teal">implementation</span>,
              </span>
              <br />
              by CRE practitioners.
            </h1>
            <p className="mt-7 text-lg sm:text-xl text-gsp-text-body leading-relaxed max-w-2xl mx-auto">
              We ship AI on live CRE deals every&nbsp;day. We'll help you do the same.
            </p>
            <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
              <button onClick={() => scrollToSection('contact')} className={btnPrimary}>
                Book a consult
              </button>
              <button onClick={() => scrollToSection('how')} className={btnSecondary}>
                How we work
              </button>
            </div>
          </MotionDiv>
        </div>
      </section>

      {/* Services — two tracks */}
      <section id="services" className="py-20 sm:py-28 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-14">
            <div className="text-xs uppercase tracking-[0.18em] text-gsp-teal font-semibold mb-4">Services</div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gsp-text-primary leading-tight">
              Strategy first. Then we build.
            </h2>
            <p className="text-lg text-gsp-text-body mt-5">
              Every engagement starts with a tailored AI roadmap, grounded in your firm's priorities, existing tech stack, data and document infrastructure, and the third-party providers you already rely on. Once we know where to focus, our in-house engineering team turns the plan into production systems.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Strategy */}
            <div className="bg-white rounded-2xl border border-gray-200 p-8 sm:p-10 relative overflow-hidden">
              {/* Top color stripe removed (designer review: met the
                  rounded corner awkwardly). */}
              <div className="mb-6">
                <div className="w-14 h-14 rounded-xl bg-gsp-teal/10 flex items-center justify-center">
                  <Briefcase className="w-7 h-7 text-gsp-teal" strokeWidth={1.8} />
                </div>
              </div>
              <h3 className="text-2xl sm:text-3xl font-bold text-gsp-text-primary mb-3">
                AI Strategy &amp; Foundations
              </h3>
              <p className="text-gsp-text-body leading-relaxed mb-6">
                We assess your firm's workflows, technology stack, and where AI already fits in, then deliver a prioritized roadmap tailored to your firm. We also run hands-on AI enablement for your entire team, from analysts to senior leadership.
              </p>
              <ul className="space-y-3 text-gsp-text-body">
                {[
                  'Workflow + tech-stack audit',
                  'Prioritized AI opportunity roadmap',
                  'Hands-on training: basic AI tools and CRE-specific applications (e.g., Claude suite training sessions)',
                  '180-day action plan',
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span className="mt-2 w-1.5 h-1.5 rounded-full bg-gsp-teal flex-shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Implementation */}
            <div className="bg-gsp-navy rounded-2xl p-8 sm:p-10 relative overflow-hidden text-white">
              {/* Top stripe + light grid overlay removed (designer review). */}
              <div className="relative">
                <div className="mb-6">
                  <div className="w-14 h-14 rounded-xl bg-gsp-orange/20 flex items-center justify-center">
                    <ImplementationIcon />
                  </div>
                </div>
                <h3 className="text-2xl sm:text-3xl font-bold mb-3">Implementation</h3>
                <p className="text-white/80 leading-relaxed mb-6">
                  We build and deploy solutions from your roadmap: custom AI pipelines, workflow automation, and system integration so your team can focus on deals. Built by George Smith Partners' in-house, AI-focused technology team.
                </p>
                <ul className="space-y-3 text-white/85">
                  {[
                    'Custom AI pipeline development',
                    'System integration & deployment',
                    'Team training and handoff',
                    'Ongoing support and advisory',
                  ].map((item) => (
                    <li key={item} className="flex items-start gap-3">
                      <span className="mt-2 w-1.5 h-1.5 rounded-full bg-gsp-orange flex-shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How We Work */}
      <section id="how" className="py-20 sm:py-28 bg-white border-y border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-16">
            <div className="text-xs uppercase tracking-[0.18em] text-gsp-teal font-semibold mb-4">How We Work</div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gsp-text-primary leading-tight">
              Defined outcomes. Defined deliverables. No ambiguity.
            </h2>
            <p className="text-lg text-gsp-text-body mt-5">
              We scope every engagement around outcomes you can measure. Start with your firm's specific workflows and pain points, then deliver targeted solutions your team will actually use.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
            {/* Phase 01 */}
            <div className="relative bg-white rounded-2xl border border-gray-200 p-7 sm:p-8">
              <div className="text-[10px] uppercase tracking-[0.18em] text-gsp-text-subtle font-semibold mb-1">Phase</div>
              <div className="flex items-center justify-between mb-1">
                <div className="font-bold text-gsp-teal" style={{ ...statNumStyle, fontSize: '72px', lineHeight: 0.9 }}>01</div>
                <svg className="hidden md:block w-7 h-7 text-gray-200" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                </svg>
              </div>
              <div className="text-2xl font-bold text-gsp-text-primary mb-5 mt-2">Discover</div>
              <div className="h-px w-10 bg-gsp-teal/40 mb-5" />
              <ul className="space-y-2.5 text-gsp-text-body">
                {[
                  'Stakeholder interviews across all levels',
                  'Workflow and tech-stack mapping',
                  'Pain points and quick-win identification',
                  'Basic AI tools and CRE-specific application training (e.g., Claude suite sessions)',
                ].map((item) => (
                  <li key={item} className="flex items-start gap-2">
                    <span className="text-gsp-teal mt-1">›</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Phase 02 */}
            <div className="relative bg-white rounded-2xl border border-gray-200 p-7 sm:p-8">
              <div className="text-[10px] uppercase tracking-[0.18em] text-gsp-text-subtle font-semibold mb-1">Phase</div>
              <div className="flex items-center justify-between mb-1">
                <div className="font-bold text-gsp-teal" style={{ ...statNumStyle, fontSize: '72px', lineHeight: 0.9 }}>02</div>
                <svg className="hidden md:block w-7 h-7 text-gray-200" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                </svg>
              </div>
              <div className="text-2xl font-bold text-gsp-text-primary mb-5 mt-2">Design</div>
              <div className="h-px w-10 bg-gsp-teal/40 mb-5" />
              <ul className="space-y-2.5 text-gsp-text-body">
                {[
                  'Prioritized AI opportunity roadmap',
                  'Solution architecture recommendations',
                  'Vendor and build-vs-buy analysis',
                  '180-day action plan',
                ].map((item) => (
                  <li key={item} className="flex items-start gap-2">
                    <span className="text-gsp-teal mt-1">›</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Phase 03 — navy. Top color stripe removed for consistency
                with the Services cards above. */}
            <div className="relative bg-gsp-navy rounded-2xl border border-gsp-navy p-7 sm:p-8 text-white overflow-hidden">

              <div className="text-[10px] uppercase tracking-[0.18em] text-white/60 font-semibold mb-1">Phase</div>
              <div className="flex items-center justify-between mb-1">
                <div className="font-bold text-gsp-orange" style={{ ...statNumStyle, fontSize: '72px', lineHeight: 0.9 }}>03</div>
                <span className="text-[10px] uppercase tracking-[0.18em] text-white/60 font-semibold">Ship</span>
              </div>
              <div className="text-2xl font-bold text-white mb-5 mt-2">Deliver</div>
              <div className="h-px w-10 bg-gsp-orange/60 mb-5" />
              <ul className="space-y-2.5 text-white/85">
                {[
                  'Custom AI pipeline development',
                  'System integration and deployment',
                  'Team training and handoff',
                  'Ongoing support and advisory',
                ].map((item) => (
                  <li key={item} className="flex items-start gap-2">
                    <span className="text-gsp-orange mt-1">›</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Case Study */}
      <section id="case" className="py-20 sm:py-28 bg-gradient-to-br from-gsp-navy via-gsp-navy to-gsp-navy-light relative overflow-hidden">
        <GridBgLight opacityClass="opacity-40" />
        {/* Diagonal ornament removed — one ornament per hero, none elsewhere. */}

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-4 text-white">
              <div className="text-xs uppercase tracking-[0.18em] text-gsp-orange font-semibold mb-4">Case Study</div>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight mb-5">
                Lease Abstracts for National Retail Portfolio
              </h2>
              <p className="text-white/80 text-lg leading-relaxed mb-5">
                We took Propvetter's existing lease abstract system and tailored it for a client acquiring a national CRE portfolio, processing 1,000+ tenants across 15,000+ documents. This kind of custom work cannot be done via existing chat or agentic interfaces alone.
              </p>
              <p className="text-white/70 leading-relaxed">
                The system organized a messy data room, extracted and validated structured lease terms, and generated a custom interactive dashboard of aggregate analysis across the entire portfolio.
              </p>
            </div>

            <div className="lg:col-span-8">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="bg-white/[0.06] backdrop-blur-sm border border-white/15 rounded-2xl p-6 text-white flex flex-col justify-between min-h-[140px]">
                  <div className="text-[10px] uppercase tracking-[0.18em] text-white/60 mb-4 sm:whitespace-nowrap">Total processing time</div>
                  <div className="text-4xl sm:text-5xl font-bold text-white whitespace-nowrap" style={statNumStyle}>24 hrs</div>
                </div>
                <div className="bg-white/[0.06] backdrop-blur-sm border border-white/15 rounded-2xl p-6 text-white flex flex-col justify-between min-h-[140px]">
                  <div className="text-[10px] uppercase tracking-[0.18em] text-white/60 mb-4 sm:whitespace-nowrap">Manual timeline equivalent</div>
                  <div className="text-4xl sm:text-5xl font-bold text-white whitespace-nowrap" style={statNumStyle}>3–4 mo</div>
                </div>
                <div className="bg-white/[0.06] backdrop-blur-sm border border-white/15 rounded-2xl p-6 text-white flex flex-col justify-between min-h-[140px]">
                  <div className="text-[10px] uppercase tracking-[0.18em] text-white/60 mb-4 sm:whitespace-nowrap">Estimated cost saved</div>
                  <div className="text-4xl sm:text-5xl font-bold text-gsp-orange whitespace-nowrap" style={statNumStyle}>$300K+</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact / CTA */}
      <section id="contact" className="bg-white py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative overflow-hidden bg-gsp-teal rounded-[20px]">
            <GridBgLight opacityClass="opacity-50" />
            <div className="relative px-6 sm:px-12 lg:px-16 py-16 sm:py-20">
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
                <div className="lg:col-span-5 text-white">
                  <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight mb-5">
                    Book a consult.
                  </h2>
                  <p className="text-white/85 text-lg leading-relaxed mb-8">
                    Tell us about your firm and what you'd like to explore. We'll come back with a scoped engagement, not a generic pitch.
                  </p>

                  <div className="space-y-4">
                    <a
                      href={`mailto:${CONSULTING_EMAIL}`}
                      onClick={handleEmailClick}
                      className="flex items-center gap-4 group"
                    >
                      <div className="w-11 h-11 rounded-full bg-white/15 flex items-center justify-center group-hover:bg-white/25 transition-colors">
                        <EmailIcon />
                      </div>
                      <div>
                        <div className="text-xs uppercase tracking-wider text-white/60">Email</div>
                        <div className="text-white font-medium">
                          {emailCopied ? 'Copied to clipboard!' : CONSULTING_EMAIL}
                        </div>
                      </div>
                    </a>
                    <a href="https://www.propvetter.com" className="flex items-center gap-4 group">
                      <div className="w-11 h-11 rounded-full bg-white/15 flex items-center justify-center group-hover:bg-white/25 transition-colors">
                        <GlobeIcon />
                      </div>
                      <div>
                        <div className="text-xs uppercase tracking-wider text-white/60">Platform</div>
                        <div className="text-white font-medium">www.propvetter.com</div>
                      </div>
                    </a>
                    <a href="https://www.gspartners.com" target="_blank" rel="noopener noreferrer" className="flex items-center gap-4 group">
                      <div className="w-11 h-11 rounded-full bg-white/15 flex items-center justify-center group-hover:bg-white/25 transition-colors">
                        <BuildingIcon />
                      </div>
                      <div>
                        <div className="text-xs uppercase tracking-wider text-white/60">Advisory firm</div>
                        <div className="text-white font-medium">www.gspartners.com</div>
                      </div>
                    </a>
                  </div>
                </div>

                <div className="lg:col-span-7">
                  <div className="bg-white/[0.08] backdrop-blur-md border border-white/15 rounded-2xl p-7 sm:p-9">
                    <h3 className="text-white font-bold text-lg mb-5">Tell us about your firm</h3>
                    <form className="space-y-4" onSubmit={onSubmit}>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label htmlFor="name" className="block text-white text-sm font-medium mb-1.5">
                            Name <span className="text-gsp-orange">*</span>
                          </label>
                          <input
                            id="name"
                            type="text"
                            required
                            value={form.name}
                            onChange={onChange}
                            placeholder="Your name"
                            className="w-full bg-white/10 border border-white/20 rounded-[10px] px-4 py-3 text-white placeholder-white/40 focus:outline-none focus:border-white focus:bg-white/15 transition"
                          />
                        </div>
                        <div>
                          <label htmlFor="firm" className="block text-white text-sm font-medium mb-1.5">
                            Firm <span className="text-gsp-orange">*</span>
                          </label>
                          <input
                            id="firm"
                            type="text"
                            required
                            value={form.firm}
                            onChange={onChange}
                            placeholder="Company name"
                            className="w-full bg-white/10 border border-white/20 rounded-[10px] px-4 py-3 text-white placeholder-white/40 focus:outline-none focus:border-white focus:bg-white/15 transition"
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label htmlFor="email" className="block text-white text-sm font-medium mb-1.5">
                            Email <span className="text-gsp-orange">*</span>
                          </label>
                          <input
                            id="email"
                            type="email"
                            required
                            value={form.email}
                            onChange={onChange}
                            placeholder="you@firm.com"
                            className="w-full bg-white/10 border border-white/20 rounded-[10px] px-4 py-3 text-white placeholder-white/40 focus:outline-none focus:border-white focus:bg-white/15 transition"
                          />
                        </div>
                        <div>
                          <label htmlFor="role" className="block text-white text-sm font-medium mb-1.5">Role</label>
                          <input
                            id="role"
                            type="text"
                            value={form.role}
                            onChange={onChange}
                            placeholder="Managing Director"
                            className="w-full bg-white/10 border border-white/20 rounded-[10px] px-4 py-3 text-white placeholder-white/40 focus:outline-none focus:border-white focus:bg-white/15 transition"
                          />
                        </div>
                      </div>
                      <div>
                        <label htmlFor="message" className="block text-white text-sm font-medium mb-1.5">
                          What would you like to explore?
                        </label>
                        <textarea
                          id="message"
                          rows={4}
                          value={form.message}
                          onChange={onChange}
                          placeholder="Workflows, pain points, or specific outcomes you'd like to discuss…"
                          className="w-full bg-white/10 border border-white/20 rounded-[10px] px-4 py-3 text-white placeholder-white/40 focus:outline-none focus:border-white focus:bg-white/15 transition resize-none"
                        />
                      </div>
                      <button
                        type="submit"
                        disabled={isSubmitting}
                        className={`${btnPrimary} w-full sm:w-auto disabled:opacity-60`}
                      >
                        {isSubmitting ? 'Sending…' : 'Send inquiry'}
                      </button>
                      {/* Last child: under space-y-4 an earlier position would push the visible fields down. */}
                      <BotTrapField inputRef={botTrap.inputRef} />
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}

export default Consulting;
